import { Fragment, useEffect, useMemo, useState } from "react";
import {
  emptyFilter,
  operatorsFor,
  validateFilter,
  type FieldDefinition,
  type Filter,
  type FilterCondition,
  type FilterOperator,
  type Json,
  type OperatorArity,
} from "@estelle/domain";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";

import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { groupFields } from "@/components/filters/common";
import { ValueInput } from "@/components/filters/ValueInput";
import {
  describeConditionParts,
  flatConditions,
} from "@/components/filters/describe";
import { setFieldCondition } from "@/components/filters/tree";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetClose,
  SheetDescription,
  SheetPanel,
  SheetTitle,
} from "@/components/ui/sheet";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { cn } from "@/lib/utils";

/** Below this many options, a search box is more friction than it saves. */
const SEARCH_THRESHOLD = 8;

/** Same pause as the desktop search box and `FilterInput`'s free text. */
const DEBOUNCE_MS = 250;

/**
 * A condition's value as strings — option values and free-form tokens are
 * always strings by construction, so anything else in the array (there
 * never is anything else) is dropped rather than stringified into
 * "[object Object]".
 */
function toStringArray(value: Json | undefined): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function isBlank(value: Json | undefined): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.every((item) => isBlank(item));
  return false;
}

/**
 * The one or two "any of" operators a field's Is/Is-not toggle switches
 * between.
 *
 * `is_any_of`/`is_none_of` for enum and reference; tag has only
 * `has_any_tag` and no negated form, so it gets `negated: null` and the
 * toggle is not offered — there is nothing for "Is not" to mean yet. If a
 * negated "has none of these tags" operator is ever added to the domain,
 * this starts offering it with no change here, since it reads the pair from
 * `operatorsFor` rather than naming `is_any_of`/`is_none_of` as the only
 * possibility.
 */
function manyOperators(
  type: FieldDefinition["type"],
): { positive: FilterOperator; negated: FilterOperator | null } | null {
  const many = operatorsFor(type).filter((o) => o.arity === "many");
  const positive = many[0];
  if (!positive) return null;
  return { positive: positive.operator, negated: many[1]?.operator ?? null };
}

/** The muted value line under a field's row on the "Filter by" screen. */
function mobilePreview(
  condition: FilterCondition,
  field: FieldDefinition,
  fields: FieldDefinition[],
): string | null {
  // `describeConditionParts` has no value half for a `none`-arity operator —
  // "is yes" is already the whole sentence — so boolean reads its answer
  // off the operator directly instead.
  if (field.type === "boolean") {
    return condition.operator === "is_true" ? "Yes" : "No";
  }
  return describeConditionParts(condition, fields)?.value ?? null;
}

type Screen = { kind: "list" } | { kind: "field"; field: FieldDefinition };

/**
 * The mobile "Filter by" sheet (#349).
 *
 * Below `md` there is nowhere for the desktop chip dropdown to open — a
 * floating popover has no room beside a field on a phone-width bar — so
 * tapping the filter control opens this instead: every field as a row,
 * tapping one drills into its own screen, and ticking a box or filling a
 * value updates the filter immediately, no Apply button, because that is
 * how the reference app it follows works.
 *
 * Still one filter model. `tree.ts`'s `setFieldCondition` is the only new
 * primitive this needed; everything that produces a condition — the many
 * screen's checkboxes, the scalar screen's range — hands its result to that,
 * and `ValueInput` (via `ConditionRow`'s own sibling) renders anything that
 * is not a list of options, same component the desktop popover uses.
 *
 * Deliberately narrower than the desktop builder: one operator per field
 * type rather than a choice of them — `contains` for text, `between` for a
 * range, `is_any_of`/`is_none_of` behind Is/Is-not for enum and reference —
 * which is what lets this be two screens with no operator picker at all. A
 * condition built here can always be refined further from the desktop.
 *
 * Editing a field is scoped to the flat model a chip already represents. A
 * group or an OR filter has no field-by-field form (`describe.ts`), so
 * `FilterInput` never opens this for that case, on a phone either — the
 * existing "N conditions (advanced)" chip still opens the tree editor there.
 */
export function MobileFilterSheet({
  open,
  onOpenChange,
  fields,
  value,
  initialField,
  onChange,
  common = [],
  showLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: FieldDefinition[];
  value: Filter;
  /** Drill straight into this field — a chip was tapped, not the bar itself. */
  initialField?: string;
  onChange: (next: Filter) => void;
  /** The list's common filters, listed first (#633). */
  common?: FieldDefinition[];
  /** The closing button's words: "Show 78 orders". */
  showLabel?: string;
}) {
  const filterable = useMemo(
    () => fields.filter((field) => field.filterable !== false),
    [fields],
  );
  // The common four first, then the rest in declaration order (#633).
  const { ordered, commonCount } = useMemo(() => {
    const groups = groupFields(filterable, common);
    return {
      ordered: [...groups.common, ...groups.more],
      commonCount: groups.common.length,
    };
  }, [filterable, common]);
  const conditions = useMemo(() => flatConditions(value) ?? [], [value]);

  const [screen, setScreen] = useState<Screen>({ kind: "list" });

  // Reopening always lands somewhere specific — the field a chip named, or
  // the list — never wherever the sheet happened to be left last time.
  // Derived during render rather than in an effect, the way `SectionPage`'s
  // `columnState` re-reads on a section change: comparing against what this
  // was last synced to and adjusting state right here skips the render an
  // effect would cost, and there is no external system to synchronise with,
  // only this component's own prop.
  const [syncedWith, setSyncedWith] = useState({ open, initialField });
  if (syncedWith.open !== open || syncedWith.initialField !== initialField) {
    setSyncedWith({ open, initialField });
    if (open) {
      const found = initialField
        ? filterable.find((field) => field.name === initialField)
        : undefined;
      setScreen(found ? { kind: "field", field: found } : { kind: "list" });
    }
  }

  function conditionFor(fieldName: string): FilterCondition | null {
    return conditions.find((c) => c.field === fieldName) ?? null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetPanel side="bottom" className="gap-0 p-0">
        <SheetDescription className="sr-only">
          {screen.kind === "field"
            ? `Filter by ${screen.field.label}`
            : "Choose a field to filter by"}
        </SheetDescription>

        <div className="min-h-0 flex-1">
          {screen.kind === "list" ? (
            <ListScreen
              fields={ordered}
              commonCount={commonCount}
              allFields={fields}
              conditionFor={conditionFor}
              hasAny={conditions.length > 0}
              onOpenField={(field) => {
                setScreen({ kind: "field", field });
              }}
              onResetAll={() => {
                onChange(emptyFilter());
              }}
            />
          ) : (
            <FieldScreen
              field={screen.field}
              condition={conditionFor(screen.field.name)}
              onBack={() => {
                setScreen({ kind: "list" });
              }}
              onCommit={(condition) => {
                onChange(
                  setFieldCondition(value, screen.field.name, condition),
                );
              }}
            />
          )}
        </div>

        {/*
          The way back to the list, saying how many rows it now holds (#633):
          every change above applies as it is made, so this only closes.
        */}
        <div className="shrink-0 border-t p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <SheetClose asChild>
            <Button type="button" className="min-h-11 w-full">
              {showLabel ?? "Show results"}
            </Button>
          </SheetClose>
        </div>
      </SheetPanel>
    </Sheet>
  );
}

function Header({
  leading,
  leadingLabel,
  onBack,
  title,
  onReset,
  resetDisabled,
}: {
  leading: "close" | "back";
  leadingLabel: string;
  onBack?: () => void;
  title: string;
  onReset: () => void;
  resetDisabled: boolean;
}) {
  const leadingButton = (
    <Button
      size="icon-sm"
      variant="ghost"
      className="rounded-full"
      aria-label={leadingLabel}
      onClick={onBack}
    >
      {leading === "close" ? <X /> : <ChevronLeft />}
    </Button>
  );

  return (
    /*
      Three columns, the outer two the same width whenever the title leaves
      room for it, so the title stays centred. The right one holds two controls
      now — Feedback as well as Reset (#511) — which a fixed-width slot could
      not take: this sheet is modal, so the page header's Feedback button is
      under its overlay while it is open.
    */
    <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-b px-2 py-3">
      <div className="flex justify-start">
        {leading === "close" ? (
          <SheetClose asChild>{leadingButton}</SheetClose>
        ) : (
          leadingButton
        )}
      </div>

      <SheetTitle className="min-w-0 truncate text-center text-base font-semibold">
        {title}
      </SheetTitle>

      <div className="flex items-center justify-end gap-1">
        <FeedbackButton />
        <Button
          size="sm"
          variant="ghost"
          disabled={resetDisabled}
          onClick={onReset}
        >
          Reset
        </Button>
      </div>
    </div>
  );
}

function ListScreen({
  fields,
  commonCount,
  allFields,
  conditionFor,
  hasAny,
  onOpenField,
  onResetAll,
}: {
  fields: FieldDefinition[];
  /** How many of `fields`, from the start, are the list's common filters. */
  commonCount: number;
  allFields: FieldDefinition[];
  conditionFor: (name: string) => FilterCondition | null;
  hasAny: boolean;
  onOpenField: (field: FieldDefinition) => void;
  onResetAll: () => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <Header
        leading="close"
        leadingLabel="Close"
        title="Filter by"
        onReset={onResetAll}
        resetDisabled={!hasAny}
      />
      <div className="flex-1 overflow-auto">
        {fields.map((field, index) => {
          const condition = conditionFor(field.name);
          const preview = condition
            ? mobilePreview(condition, field, allFields)
            : null;
          const heading =
            commonCount > 0 && index === 0
              ? "Common"
              : commonCount > 0 && index === commonCount
                ? "More"
                : null;
          return (
            <Fragment key={field.name}>
              {heading ? (
                <p className="bg-muted/40 text-muted-foreground border-b px-4 py-1.5 text-[11px] font-semibold tracking-wide uppercase">
                  {heading}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  onOpenField(field);
                }}
                className="hover:bg-accent flex w-full items-center justify-between gap-3 border-b px-4 py-3.5 text-left"
              >
                <span className="min-w-0">
                  <span className="block text-sm">{field.label}</span>
                  {preview ? (
                    <span className="text-muted-foreground block truncate text-xs">
                      {preview}
                    </span>
                  ) : null}
                </span>
                <ChevronRight
                  className="text-muted-foreground size-4 shrink-0"
                  aria-hidden
                />
              </button>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function FieldScreen({
  field,
  condition,
  onBack,
  onCommit,
}: {
  field: FieldDefinition;
  condition: FilterCondition | null;
  onBack: () => void;
  onCommit: (condition: FilterCondition | null) => void;
}) {
  const many = manyOperators(field.type);

  return (
    <div className="flex h-full flex-col">
      <Header
        leading="back"
        leadingLabel="Back to Filter by"
        onBack={onBack}
        title={field.label}
        onReset={() => {
          onCommit(null);
        }}
        resetDisabled={condition === null}
      />
      {/*
        Keyed by field: this is a fresh screen every time, and each panel
        below keeps a little state of its own (the Is/Is-not toggle, the
        options search, a still-being-typed range) that belongs to whichever
        field is open, not to whichever field opened last in this position.
      */}
      <div className="flex-1 overflow-auto">
        {many ? (
          <ManyPanel
            key={field.name}
            field={field}
            many={many}
            condition={condition}
            onCommit={onCommit}
          />
        ) : field.type === "boolean" ? (
          <BooleanPanel
            key={field.name}
            field={field}
            condition={condition}
            onCommit={onCommit}
          />
        ) : (
          <ScalarPanel
            key={field.name}
            field={field}
            condition={condition}
            onCommit={onCommit}
          />
        )}
      </div>
    </div>
  );
}

function ManyPanel({
  field,
  many,
  condition,
  onCommit,
}: {
  field: FieldDefinition;
  many: { positive: FilterOperator; negated: FilterOperator | null };
  condition: FilterCondition | null;
  onCommit: (condition: FilterCondition | null) => void;
}) {
  const [search, setSearch] = useState("");
  // Its own state rather than read off `condition.operator`: the toggle has
  // to mean something the moment it is tapped, before anything is ticked —
  // reading it off the condition instead left it silently reverting to
  // "Is" if someone tapped "Is not" first and only then ticked a box, since
  // zero ticks commits no condition at all for it to read back from.
  const [negated, setNegated] = useState(
    condition !== null && condition.operator === many.negated,
  );
  const selected = new Set(toStringArray(condition?.value));
  const options = field.options ?? [];

  function apply(nextSelected: Set<string>, nextNegated: boolean) {
    if (nextSelected.size === 0) {
      onCommit(null);
      return;
    }
    const operator = nextNegated && many.negated ? many.negated : many.positive;
    onCommit({
      kind: "condition",
      field: field.name,
      operator,
      value: [...nextSelected],
    });
  }

  // No predeclared options — a free-form tag, or a reference whose list
  // loads elsewhere. `ValueInput` already knows this case: the same
  // `TokenInput` the desktop popover renders, one operator, nothing to
  // search or tick.
  if (options.length === 0) {
    return (
      <div className="p-4">
        <ValueInput
          field={field}
          arity="many"
          value={condition?.value}
          onChange={(nextValue) => {
            const values = toStringArray(nextValue);
            onCommit(
              values.length === 0
                ? null
                : {
                    kind: "condition",
                    field: field.name,
                    operator: many.positive,
                    value: values,
                  },
            );
          }}
        />
      </div>
    );
  }

  const query = search.trim().toLowerCase();
  const visible =
    query === ""
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(query));

  return (
    <div className="flex flex-col">
      {options.length > SEARCH_THRESHOLD ? (
        <div className="p-3">
          <div className="relative">
            <Search
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder="Search options…"
              className="pl-8"
            />
          </div>
        </div>
      ) : null}

      {many.negated ? (
        <div className="flex gap-2 px-3 pb-3">
          {(
            [
              { label: "Is", value: false },
              { label: "Is not", value: true },
            ] as const
          ).map((option) => (
            <button
              key={option.label}
              type="button"
              aria-pressed={negated === option.value}
              onClick={() => {
                setNegated(option.value);
                apply(selected, option.value);
              }}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                negated === option.value
                  ? SELECTED_CHOICE
                  : "text-muted-foreground hover:bg-accent",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex flex-col">
        {visible.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm">
            No options match “{search}”.
          </p>
        ) : (
          visible.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-3 border-b px-4 py-3"
            >
              <Checkbox
                checked={selected.has(option.value)}
                onCheckedChange={(checked) => {
                  const next = new Set(selected);
                  if (checked) next.add(option.value);
                  else next.delete(option.value);
                  apply(next, negated);
                }}
              />
              <span className="text-sm">{option.label}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

function BooleanPanel({
  field,
  condition,
  onCommit,
}: {
  field: FieldDefinition;
  condition: FilterCondition | null;
  onCommit: (condition: FilterCondition | null) => void;
}) {
  const current = condition?.operator ?? null;
  const options = [
    { label: "Yes", operator: "is_true" as const },
    { label: "No", operator: "is_false" as const },
  ];

  return (
    <div className="flex flex-col gap-2 p-4">
      {options.map((option) => (
        <button
          key={option.operator}
          type="button"
          aria-pressed={current === option.operator}
          onClick={() => {
            // Tapping the selected one again clears it — the same escape
            // hatch Reset offers, one tap closer.
            onCommit(
              current === option.operator
                ? null
                : {
                    kind: "condition",
                    field: field.name,
                    operator: option.operator,
                  },
            );
          }}
          className={cn(
            "rounded-md border px-4 py-3 text-left text-sm",
            current === option.operator ? SELECTED_CHOICE : "hover:bg-accent",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ScalarPanel({
  field,
  condition,
  onCommit,
}: {
  field: FieldDefinition;
  condition: FilterCondition | null;
  onCommit: (condition: FilterCondition | null) => void;
}) {
  const operator: FilterOperator =
    field.type === "text" ? "contains" : "between";
  const arity: OperatorArity = field.type === "text" ? "one" : "two";
  const seed = condition?.operator === operator ? condition.value : undefined;

  const [draft, setDraft] = useState<Json | undefined>(seed);

  // Resynced during render rather than in an effect, same reasoning as
  // `MobileFilterSheet`'s own screen state just above: opening a different
  // field, or this one being cleared by Reset out from under it, both show
  // up as `seed` changing, and there is nothing external to synchronise
  // with here, only this component's own props.
  const [syncedSeed, setSyncedSeed] = useState({ fieldName: field.name, seed });
  if (syncedSeed.fieldName !== field.name || syncedSeed.seed !== seed) {
    setSyncedSeed({ fieldName: field.name, seed });
    setDraft(seed);
  }

  /*
    What is wrong with the draft, in the desktop editor's own words: the same
    `validateFilter` that disables Apply there. A range with one end typed, or
    with its start after its end, matches nothing, so it is not applied — the
    list used to empty the moment the first date was typed and stay empty until
    the second arrived, with nothing to say why (#518).
  */
  const problem = useMemo(() => {
    if (isBlank(draft)) return null;
    return (
      validateFilter(
        {
          kind: "group",
          combinator: "and",
          children: [
            { kind: "condition", field: field.name, operator, value: draft },
          ],
        },
        [field],
      )[0]?.message ?? null
    );
  }, [draft, field, operator]);

  useEffect(() => {
    // A resync from outside lands here too, since it is also a change to
    // `draft` — compared by content against `seed` rather than skipped with
    // a "was this just synced" flag, so a value typed back to exactly what
    // is already applied is correctly treated as nothing to commit either.
    if (JSON.stringify(draft) === JSON.stringify(seed)) return;
    // Cleared is applied; incomplete or backwards waits for the person to mend
    // it, and the filter in force stays as it was meanwhile.
    if (!isBlank(draft) && problem !== null) return;
    const timer = setTimeout(() => {
      onCommit(
        isBlank(draft)
          ? null
          : { kind: "condition", field: field.name, operator, value: draft },
      );
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [draft, seed, problem, field.name, operator, onCommit]);

  return (
    <div className="p-4">
      <ValueInput
        field={field}
        arity={arity}
        value={draft}
        onChange={setDraft}
      />
      {problem !== null ? (
        <p role="alert" className="text-destructive mt-2 text-xs">
          {problem}
        </p>
      ) : null}
    </div>
  );
}
